import { useEffect, useRef, useState } from "react"

import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { X } from "lucide-react"

import { MetricValuePicker } from "@/components/filters/MetricValuePicker"

import {
  DEFAULT_CONVERSATIONS_METRIC_VALUE_OPERATOR,
  UPLOADED_CONVERSATION_METRIC_VALUE_OPERATORS,
  UPLOADED_CONVERSATION_METRIC_VALUE_TYPE,
  type MetricValueFilter,
} from "../../types"
import type { MetricFilterOption } from "../../utils/metricFilterOptions"

interface MetricFilterPanelProps {
  metrics: MetricFilterOption[]
  metricIds: string[]
  filters: MetricValueFilter[]
  selectedId: string
  onSelect: (id: string) => void
  onApply: (id: string, filter: MetricValueFilter | null | undefined) => void
  loading?: boolean
  error?: boolean
  onRetry: () => void
  stringValues?: string[]
  valuesLoading?: boolean
  valuesError?: boolean
  onRetryValues: () => void
}

function describeFilter(filter: MetricValueFilter) {
  return filter.operator === "exists"
    ? "Any value"
    : `${filter.operator} ${filter.value === "" ? "(empty)" : filter.value}`
}

export function MetricFilterPanel(props: MetricFilterPanelProps) {
  const {
    metrics,
    filters,
    selectedId,
    onSelect,
    onApply,
    loading,
    error,
    onRetry,
  } = props
  const selected = metrics.find((metric) => metric.id === selectedId)
  const nameFor = (id: string) =>
    metrics.find((metric) => metric.id === id)?.name ??
    filters.find((filter) => filter.metricId === id)?.metricDisplayName ??
    id
  return (
    <div className="w-80 space-y-3 p-3">
      {!selected && filters.length > 0 && (
        <div
          className="max-h-32 space-y-1 overflow-y-auto"
          aria-label="Active metric filters"
        >
          {filters.map((filter) => (
            <div
              key={filter.metricId}
              className="flex items-center gap-1 rounded-md bg-surface-1"
            >
              <Button
                variant="ghost"
                className="min-w-0 flex-1 justify-start"
                disabled={
                  !metrics.some((metric) => metric.id === filter.metricId)
                }
                onClick={() => onSelect(filter.metricId)}
              >
                <span className="truncate">
                  {nameFor(filter.metricId)}: {describeFilter(filter)}
                </span>
              </Button>
              <Button
                icon
                variant="ghost"
                tooltip={`Remove ${nameFor(filter.metricId)} filter`}
                onClick={() => {
                  onApply(filter.metricId, undefined)
                  if (selectedId === filter.metricId) {
                    onSelect("")
                  }
                }}
              >
                <X />
              </Button>
            </div>
          ))}
        </div>
      )}
      {error && (
        <div role="alert" className="text-sm text-coval-danger-text">
          Could not load metrics.{" "}
          <Button variant="link" onClick={onRetry}>
            Retry metrics
          </Button>
        </div>
      )}
      <MetricValuePicker
        items={metrics.map((metric) => ({
          id: metric.id,
          label: metric.name,
          searchValue: metric.searchText,
        }))}
        selectedId={selectedId}
        onSelect={onSelect}
        loading={loading}
      >
        {selected?.outputType === "set" ? (
          <p className="text-xs text-coval-text-muted">
            This metric type does not support value filtering yet. Use Has
            Metric to filter for its presence.
          </p>
        ) : selected ? (
          <MetricConditionEditor
            key={selected.id}
            {...props}
            metric={selected}
          />
        ) : null}
      </MetricValuePicker>
    </div>
  )
}

function MetricConditionEditor({
  metric,
  filters,
  onApply,
  onSelect,
  stringValues,
  valuesLoading,
  valuesError,
  onRetryValues,
}: MetricFilterPanelProps & { metric: MetricFilterOption }) {
  const conditionRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    conditionRef.current?.focus()
  }, [])
  const existing = filters.find((filter) => filter.metricId === metric.id)
  const [operator, setOperator] = useState<MetricValueFilter["operator"]>(
    existing?.operator ?? DEFAULT_CONVERSATIONS_METRIC_VALUE_OPERATOR,
  )
  const [value, setValue] = useState<string | null>(existing?.value ?? null)
  const needsValue = operator !== "exists"
  const isNumeric = metric.outputType === "float"
  const hasDefinedValues = Boolean(metric.valueOptions?.length)
  const numericOperators = isNumeric && !hasDefinedValues
  const conditionUnchanged =
    existing?.operator === operator && existing.value === value
  const atLimit = filters.length >= 50 && !existing
  const valid =
    !atLimit &&
    (conditionUnchanged ||
      numericOperators ||
      !needsValue ||
      operator === "=") &&
    (!needsValue ||
      (value !== null &&
        (!isNumeric ||
          (value.trim() !== "" && Number.isFinite(Number(value))))))
  const options = [
    ...new Set([...(stringValues ?? []), ...(value !== null ? [value] : [])]),
  ]
  function apply() {
    if (!valid) {
      return
    }
    onApply(metric.id, {
      metricId: metric.id,
      metricDisplayName: metric.name,
      operator,
      value: needsValue ? (value ?? "") : "",
      valueType:
        conditionUnchanged && existing
          ? existing.valueType
          : isNumeric
            ? UPLOADED_CONVERSATION_METRIC_VALUE_TYPE.FLOAT
            : UPLOADED_CONVERSATION_METRIC_VALUE_TYPE.STRING,
    })
    onSelect("")
  }
  return (
    <div
      role="presentation"
      className="space-y-3"
      onKeyDown={(event) => {
        if (event.key === "Enter" && event.target instanceof HTMLInputElement) {
          event.preventDefault()
          apply()
        }
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor={`metric-condition-${metric.id}`}>Condition</Label>
        <Select
          value={operator}
          onValueChange={(next) => setOperator(next as typeof operator)}
        >
          <SelectTrigger
            id={`metric-condition-${metric.id}`}
            ref={conditionRef}
            aria-label="Metric condition"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {metric.outputType !== "set" && (
              <>
                <SelectItem value="exists">Any value</SelectItem>
                {(numericOperators
                  ? UPLOADED_CONVERSATION_METRIC_VALUE_OPERATORS.filter(
                      (op) => op !== "exists",
                    )
                  : ["="]
                ).map((op) => (
                  <SelectItem key={op} value={op}>
                    {op === "=" ? "Equals" : op}
                  </SelectItem>
                ))}
              </>
            )}
            {existing &&
              !numericOperators &&
              !["=", "exists"].includes(existing.operator) && (
                <SelectItem value={existing.operator}>
                  {existing.operator} (saved condition)
                </SelectItem>
              )}
          </SelectContent>
        </Select>
      </div>
      {needsValue && (
        <Label htmlFor={`metric-value-${metric.id}`}>
          {isNumeric && metric.unit ? `Value (${metric.unit})` : "Value"}
        </Label>
      )}
      {operator === "exists" && (
        <p className="text-xs text-coval-text-muted">
          Has a recorded value, including zero or an empty string.
        </p>
      )}
      {needsValue && hasDefinedValues && (
        <Select
          value={value === null ? "" : JSON.stringify(value)}
          onValueChange={(encoded) => {
            setValue(JSON.parse(encoded) as string)
            setOperator("=")
          }}
        >
          <SelectTrigger
            id={`metric-value-${metric.id}`}
            aria-label="Metric value"
          >
            <SelectValue placeholder="Choose a value…" />
          </SelectTrigger>
          <SelectContent>
            {metric.valueOptions?.map((option) => (
              <SelectItem
                key={option.value}
                value={JSON.stringify(option.value)}
              >
                {option.label}
              </SelectItem>
            ))}
            {value !== null &&
              !metric.valueOptions?.some(
                (option) => option.value === value,
              ) && (
                <SelectItem value={JSON.stringify(value)}>
                  Saved value: {value === "" ? "(empty)" : value}
                </SelectItem>
              )}
          </SelectContent>
        </Select>
      )}
      {needsValue && !hasDefinedValues && (
        <>
          <Input
            id={`metric-value-${metric.id}`}
            aria-label={isNumeric ? "Metric threshold value" : "Metric value"}
            type={isNumeric ? "number" : "text"}
            step={isNumeric ? "any" : undefined}
            placeholder={
              isNumeric ? "Enter a number…" : "Enter an exact value…"
            }
            value={value ?? ""}
            onChange={(event) => setValue(event.target.value)}
          />
          {!isNumeric && (
            <>
              <Select
                value={value === null ? "" : JSON.stringify(value)}
                onValueChange={(encoded) =>
                  setValue(JSON.parse(encoded) as string)
                }
              >
                <SelectTrigger
                  aria-label="Recorded metric values"
                  disabled={!options.length}
                >
                  <SelectValue
                    placeholder={
                      valuesLoading
                        ? "Loading recorded values…"
                        : "Choose a recorded value…"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {options.map((option) => (
                    <SelectItem key={option} value={JSON.stringify(option)}>
                      {option === "" ? "(empty)" : option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {valuesError && (
                <p role="alert" className="text-xs text-coval-danger-text">
                  Could not load recorded values. You can still enter a value.{" "}
                  <Button variant="link" onClick={onRetryValues}>
                    Retry values
                  </Button>
                </p>
              )}
            </>
          )}
        </>
      )}
      {atLimit && (
        <p className="text-xs text-coval-danger-text">
          A maximum of 50 conditions of this kind is supported. Remove a
          condition before adding another.
        </p>
      )}
      <div className="flex justify-end">
        <Button onClick={apply} disabled={!valid}>
          {existing ? "Update value filter" : "Add value filter"}
        </Button>
      </div>
    </div>
  )
}
